import {
  PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaRegCopy } from "react-icons/fa";
import { HiCheck } from "react-icons/hi";

export interface CopyableTextProps {
  standalone?: boolean;
  onCopy?: () => void;
}

export const CopyableText = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<CopyableTextProps>
>((props, ref) => {
  const { standalone, onCopy, children } = props;

  const pre = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(pre.current?.innerText || "");
    if (onCopy) {
      onCopy();
    }
    setCopied(true);
  }, [setCopied, onCopy]);
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const preClasses = standalone
    ? "whitespace-normal"
    : "overflow-auto no-scroll-bar";

  return (
    <div
      className={`bg-gray-100 flex ${standalone ? "p-2 rounded my-2" : "p-4"}`}
    >
      <pre ref={pre} className={`grow text-xs ${preClasses}`}>
        {children}
      </pre>
      <div className="border-l border-gray-300 mx-4 shrink-0"></div>
      <button className="w-5 shrink-0" onClick={copy} ref={ref}>
        {copied ? <HiCheck /> : <FaRegCopy />}
        <span className="sr-only">Copy to clipboard</span>
      </button>
    </div>
  );
});

CopyableText.displayName = "CopyableText";
